import { useMemo, useState } from 'react'

import type { CodeRequestedResponse } from '@/bindings/CodeRequestedResponse'
import { ApiError } from '@/lib/api'

/** The last code request's answer, plus when it was made (the countdown's zero). */
export interface CodeStatus {
  response: CodeRequestedResponse
  requestedAt: number
}

/** Each flow has its own daily budget on the server, so its own record here. */
export type CodeFlow = 'register' | 'reset' | 'identifier'

const STORAGE_PREFIX = 'auth.code:'
/** The server's daily window: an older record says nothing about today's budget. */
const STATUS_TTL_MS = 24 * 60 * 60 * 1000

function read(storageKey: string): CodeStatus | null {
  try {
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) return null
    const status = JSON.parse(raw) as CodeStatus
    if (Date.now() - status.requestedAt > STATUS_TTL_MS) {
      sessionStorage.removeItem(storageKey)
      return null
    }
    return status
  } catch {
    return null
  }
}

/**
 * Remembers what the server said about the code for `key` (the typed
 * identifier) in `flow`, per tab, so `?step=verify` survives a refresh with
 * its countdown and action line intact. Editing the identifier looks up
 * that identifier's own status — none means the generic copy. `clear` on a
 * successful verify: the record is about a code that no longer exists.
 */
export function useCodeStatus(
  flow: CodeFlow,
  key: string,
): { status: CodeStatus | null; record: (response: CodeRequestedResponse) => void; clear: () => void } {
  const storageKey = `${STORAGE_PREFIX}${flow}:${key}`
  const stored = useMemo(() => read(storageKey), [storageKey])
  const [override, setOverride] = useState<{ key: string; status: CodeStatus | null } | null>(null)
  const write = (status: CodeStatus | null) => {
    try {
      if (status) sessionStorage.setItem(storageKey, JSON.stringify(status))
      else sessionStorage.removeItem(storageKey)
    } catch {
      // Storage unavailable: the in-memory status still drives this render.
    }
    setOverride({ key: storageKey, status })
  }
  return {
    status: override?.key === storageKey ? override.status : stored,
    record: (response) => write({ response, requestedAt: Date.now() }),
    clear: () => write(null),
  }
}

/** A verify attempt the server turned down for the code itself (wrong, spent, or expired). */
export function codeRejected(error: unknown): boolean {
  return error instanceof ApiError && error.code.startsWith('VERIFICATION_')
}
